import type { User } from "firebase/auth";
import { isFirebaseConfigured } from "@/auth/firebase";
import { loadCharacterProfile } from "@/character/storage";
import { callGetRaidStatus } from "@/cloud/game-callables";
import { syncPlayerPublicStats } from "@/cloud/sync-player-stats";
import { buyDungeonFloorSlot } from "@/dungeon/buy-dungeon-floor";
import {
  DUNGEON_FLOOR_CASH_COST,
  DUNGEON_MAX_FLOORS_CAP,
  loadDungeonMeta,
  saveDungeonMeta,
} from "@/dungeon/dungeon-meta";
import { buildDungeonPublishPayload, publishDungeonToCloud } from "@/dungeon/dungeon-publish";
import { hydrateDungeonFloorConfigs } from "@/dungeon/dungeon-floor-config";
import { validateDungeonFloorsForPublish } from "@/dungeon/dungeon-publish-validate";
import { openDungeonFloorModal } from "@/mount-dungeon-floor-modal";
import { HOME_ASSETS } from "@/home/home-assets";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { openDungeonTrialModal } from "@/mount-dungeon-trial-modal";

export type MountDungeonPageOptions = {
  asModal?: boolean;
  onRequestClose?: () => void;
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function renderOwnedFloorRow(floor: number): string {
  return `
    <li class="dungeon-floor-row">
      <button type="button" class="dungeon-floor-main" data-edit-floor="${floor}">
        <span class="dungeon-floor-badge" aria-hidden="true">${floor}</span>
        <span class="dungeon-floor-text">
          <span class="dungeon-floor-name">ด่าน ${floor}</span>
          <span class="dungeon-floor-sub">แก้ไขมอนสเตอร์และฉาก</span>
        </span>
        <span class="dungeon-floor-chevron" aria-hidden="true">›</span>
      </button>
      <button type="button" class="dungeon-floor-trial" data-trial-floor="${floor}" title="ทดลองด่าน ${floor}">
        ทดลอง
      </button>
    </li>
  `;
}

function renderLockedFloorRow(floor: number, canAfford: boolean): string {
  return `
    <li class="dungeon-floor-row dungeon-floor-row--locked">
      <button type="button" class="dungeon-floor-buy" data-buy-floor="${floor}" ${canAfford ? "" : "disabled"}>
        <span class="dungeon-floor-badge dungeon-floor-badge--locked" aria-hidden="true">🔒</span>
        <span class="dungeon-floor-text">
          <span class="dungeon-floor-name">ด่าน ${floor}</span>
          <span class="dungeon-floor-sub">ปลดล็อก · ${DUNGEON_FLOOR_CASH_COST.toLocaleString("en-US")} Cash</span>
        </span>
      </button>
    </li>
  `;
}

export async function mountDungeonPage(
  root: HTMLElement,
  user: User,
  options?: MountDungeonPageOptions,
): Promise<() => void> {
  if (options?.asModal) {
    root.classList.add("page-dungeon-lobby");
  } else {
    root.className = "page-shell page-dungeon";
  }
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  let meta = loadDungeonMeta(user.uid);
  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();
  let raidPoints = "—";
  let raidMax = 20;
  let status = "";

  const persistEconomy = () => {
    void saveHomeEconomyToIdb(user.uid, economy);
  };

  const refreshRaidPoints = async () => {
    if (!isFirebaseConfigured()) {
      raidPoints = "ออฟไลน์";
      return;
    }
    try {
      const p = await callGetRaidStatus();
      raidPoints = String(p.raidPoints);
      raidMax = p.raidPointsMax;
    } catch {
      raidPoints = "?";
    }
  };

  const tryBuyFloor = (floor: number) => {
    if (floor !== meta.maxFloors + 1) return;
    const res = buyDungeonFloorSlot(economy, meta);
    if (!res.ok) {
      status = res.reason;
      render();
      return;
    }
    economy = res.economy;
    meta = res.meta;
    saveDungeonMeta(user.uid, meta);
    persistEconomy();
    status = `ปลดด่าน ${floor} แล้ว · Cash เหลือ ${economy.wallet.cash.toLocaleString("en-US")}`;
    render();
  };

  const render = () => {
    const ownedFloors = Array.from({ length: meta.maxFloors }, (_, i) => i + 1);
    const floorRows = ownedFloors.map((f) => renderOwnedFloorRow(f)).join("");
    const nextFloor = meta.maxFloors + 1;
    const showLocked =
      meta.maxFloors < DUNGEON_MAX_FLOORS_CAP
        ? renderLockedFloorRow(nextFloor, economy.wallet.cash >= DUNGEON_FLOOR_CASH_COST)
        : "";

    const closeBtn = options?.asModal
      ? `<button type="button" class="dungeon-frame-close" id="dungeon-close" aria-label="ปิด">×</button>`
      : "";

    const footNav = options?.asModal
      ? `<a class="dungeon-foot-link" href="/home?rank=1">อันดับโลก</a>`
      : `<a class="dungeon-foot-link" href="/home?rank=1">อันดับโลก</a>
         <a class="dungeon-foot-link" href="/home">← บ้าน</a>`;

    root.innerHTML = `
      <main class="dungeon-lobby dungeon-lobby--game">
        <div class="dungeon-frame game-system-frame">
          <header class="dungeon-frame-head">
            <div class="dungeon-frame-title-wrap">
              <p class="dungeon-frame-kicker">ดันเจียนป้องกัน</p>
              <h1 class="dungeon-frame-title">ด่านของฉัน</h1>
            </div>
            ${closeBtn}
          </header>

          <p class="dungeon-frame-lede">ตั้งมอนสเตอร์และฉากต่อชั้น · เปิดเผยให้ถูกสุ่มโจมตี</p>

          <div class="dungeon-hud-strip" aria-label="สถานะ">
            <div class="dungeon-hud-chip">
              <span class="dungeon-hud-label">ชั้น</span>
              <strong>${meta.maxFloors}</strong>
            </div>
            <div class="dungeon-hud-chip dungeon-hud-chip--cash">
              <img src="${HOME_ASSETS.coin}" width="18" height="18" alt="" />
              <strong>${economy.wallet.cash.toLocaleString("en-US")}</strong>
            </div>
            <div class="dungeon-hud-chip dungeon-hud-chip--raid">
              <span class="dungeon-hud-label">โจมตี</span>
              <strong>${raidPoints}</strong><span class="dungeon-hud-muted">/${raidMax}</span>
            </div>
          </div>

          <p class="dungeon-status hint" role="status">${escapeHtml(status)}</p>

          <section class="dungeon-publish-panel" aria-label="เผยแพร่">
            <label class="dungeon-publish-toggle">
              <input type="checkbox" id="dungeon-published" ${meta.published ? "checked" : ""} />
              <span class="dungeon-publish-switch" aria-hidden="true"></span>
              <span class="dungeon-publish-label">เปิดด่านให้ถูกสุ่มโจมตี</span>
            </label>
            <button type="button" class="dungeon-publish-save" id="dungeon-publish-btn">บันทึกและเผยแพร่</button>
          </section>

          <section class="dungeon-floor-list" aria-labelledby="dungeon-floor-list-title">
            <h2 id="dungeon-floor-list-title" class="dungeon-section-title">รายการด่าน</h2>
            <ul class="dungeon-floor-rows">
              ${floorRows}
              ${showLocked}
            </ul>
          </section>

          <footer class="dungeon-frame-foot">${footNav}</footer>
        </div>
      </main>
    `;

    root.querySelector("#dungeon-close")?.addEventListener("click", () => {
      options?.onRequestClose?.();
    });

    root.querySelectorAll<HTMLButtonElement>("[data-edit-floor]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const floor = Number(btn.dataset.editFloor);
        if (!Number.isFinite(floor)) return;
        options?.onRequestClose?.();
        void openDungeonFloorModal(user, floor, { returnToDungeonLobby: true });
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-trial-floor]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const floor = Number(btn.dataset.trialFloor);
        if (!Number.isFinite(floor)) return;
        options?.onRequestClose?.();
        void openDungeonTrialModal(user, { onlyFloor: floor });
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-buy-floor]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const floor = Number(btn.dataset.buyFloor);
        if (!Number.isFinite(floor)) return;
        tryBuyFloor(floor);
      });
    });

    root.querySelector("#dungeon-publish-btn")?.addEventListener("click", () => {
      void (async () => {
        const published = root.querySelector<HTMLInputElement>("#dungeon-published")?.checked;
        meta = { ...meta, published: published === true };
        saveDungeonMeta(user.uid, meta);
        if (meta.published) {
          await hydrateDungeonFloorConfigs(user.uid);
          const preview = await buildDungeonPublishPayload(user, meta);
          const check = validateDungeonFloorsForPublish(meta, preview.floors);
          if (!check.ok) {
            status = check.reason;
            meta = { ...meta, published: false };
            saveDungeonMeta(user.uid, meta);
            render();
            return;
          }
        }
        if (!isFirebaseConfigured()) {
          status = "บันทึกในเครื่องแล้ว · ต้องตั้ง Firebase เพื่อเผยแพร่ข้ามผู้เล่น";
          render();
          return;
        }
        try {
          status = meta.published ? "กำลังย่อภาพและเผยแพร่…" : "กำลังบันทึก…";
          render();
          const result = await publishDungeonToCloud(user, meta);
          await syncPlayerPublicStats(user);
          status = meta.published
            ? result.imageCompressed
              ? "เผยแพร่ด่านแล้ว (ย่อภาพอัตโนมัติ)"
              : "เผยแพร่ด่านแล้ว"
            : "ปิดการเผยแพร่แล้ว";
        } catch (err) {
          status = err instanceof Error ? err.message : "เผยแพร่ไม่สำเร็จ";
        }
        render();
      })();
    });
  };

  await refreshRaidPoints();
  void syncPlayerPublicStats(user);
  render();
  return () => {};
}
